import { type FC, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { PageLoader } from 'src/components/PageLoader';
import { UserHelpRequest } from 'src/components/UserHelpRequest';
import { useCurrentUser } from 'src/lib/customHooks/useCurrentUser';
import { getRacemapLogoUrl } from 'src/lib/EventUtils';
import { sendPageViewEvent } from 'src/lib/googleAnalytics';
import { resolveRedirectPath } from 'src/lib/resolveRedirectPath';
import { useStore } from 'src/store/reducers';
import { Header } from '../components/Headers/Header';

const publicRoutes = [
  /^\/auth\/login$/,
  /^\/auth\/logout$/,
  /^\/auth\/register$/,
  /^\/auth\/reset-password$/,
  /^\/auth\/forgot-password$/,
  /^\/auth\/confirm-email$/,
  /^\/app$/,
  /^\/app\/redeem_key$/,
  /^\/app\/[^/]+$/,
  /^\/app\/[^/]+\/redeem_key$/,
  /^\/timing\/[^/]+$/,
  /^\/safemap\/[^/]+$/,
];

export const RootView: FC = () => {
  const currentUser = useCurrentUser();
  const isLoadingCurrentUser = useStore((s) => s.users.isLoadingCurrentUser);
  const isAuthenticated = currentUser != null;
  const location = useLocation();

  useEffect(() => {
    sendPageViewEvent(location);
  }, [location]);

  if (isLoadingCurrentUser) return <PageLoader />;
  if (isAuthenticated && location.pathname === '/auth/login') {
    return <Navigate to={resolveRedirectPath(location.state)} replace />;
  }
  const fullPath = location.pathname + location.search;
  if (!isAuthenticated && location.pathname === '/admin/events/new') {
    return <Navigate to="/auth/register" replace state={{ nextPathname: fullPath }} />;
  }
  if (!isAuthenticated && !publicRoutes.some((route) => route.test(location.pathname))) {
    return <Navigate to="/auth/login" replace state={{ nextPathname: fullPath }} />;
  }

  return (
    <>
      <RootHelmet />
      <Header />
      <Outlet />
      <UserHelpRequest />
    </>
  );
};

const RootHelmet: FC = () => {
  return (
    <Helmet>
      <meta charSet="utf-8" />
      <link rel="icon" href="https://static-cdn.racemap.com/img/favicon.png" type="image/png" />
      <meta
        name="description"
        content="GPS Live-Tracking for Sports Events. Racemap streams your race live to the Internet, smartphones and Facebook. For athletes, spectators and organizers."
      />
      <meta
        name="keywords"
        content="racemap, live, tracking, livetracking, realtime, gps, sports, event, race, competition, tracker, visualization, player, stream, replay, track, trace, athlete, spectator, app, smartphone, location, locate, running, biking, marathon, triathlon, skiing, crosscountry, trail, ultra"
      />
      <title>Racemap | Admin</title>
      <link rel="canonical" href="https://racemap.com/admin" />
      <meta property="og:title" content="Racemap | Admin" />
      <meta
        property="og:description"
        content="GPS Live-Tracking for Sports Events. Racemap streams your race live to the Internet, smartphones and Facebook. For athletes, spectators and organizers."
      />
      <meta property="og:url" content="https://racemap.com/admin" />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={getRacemapLogoUrl()} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Racemap | Admin" />
      <meta name="twitter:image" content={getRacemapLogoUrl()} />
      <meta
        name="twitter:description"
        content="GPS Live-Tracking for Sports Events. Racemap streams your race live to the Internet, smartphones and Facebook. For athletes, spectators and organizers."
      />
    </Helmet>
  );
};
